import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { SectionEyebrow, FadeInSection } from "../components/SharedSections";

const blogPosts = [
  {
    title: "Tailored Group Benefits That Power Business Growth and Employee Satisfaction",
    image: "https://static.wixstatic.com/media/8cad63_8121fd7757404ba080360be247610de3~mv2.jpg/v1/fill/w_600,h_400,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/8cad63_8121fd7757404ba080360be247610de3~mv2.jpg",
    excerpt: "Mayker Financial offers tailored group benefits in Victoria BC, boosting talent attraction, employee satisfaction, and cost control through custom plans, ongoing management, and expert consultations.",
    link: "https://www.maykerfinancial.com/post/tailored-group-benefits-that-power-business-growth-and-employee-satisfaction",
    date: "November 2024"
  },
  {
    title: "Comprehensive Family Financial Planning in Victoria, BC: A Step-by-Step Guide",
    image: "https://static.wixstatic.com/media/8cad63_866967cb7fe74659b98a35c5eeb9b289~mv2.jpg/v1/fill/w_600,h_400,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/8cad63_866967cb7fe74659b98a35c5eeb9b289~mv2.jpg",
    excerpt: "A step-by-step guide for Victoria families covers goal setting, cash flow, debt reduction, insurance, estate planning, benefits, tax-efficient investing, and retirement strategies.",
    link: "https://www.maykerfinancial.com/post/comprehensive-family-financial-planning-in-victoria-bc-a-step-by-step-guide",
    date: "October 2024"
  },
  {
    title: "Why Local Financial Expertise Matters: How Mayker Financial Supports Victoria Families",
    image: "https://static.wixstatic.com/media/8cad63_b8dc3dd47c0a40089b53a68ce2d1eeb5~mv2.jpg/v1/fill/w_600,h_400,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/8cad63_b8dc3dd47c0a40089b53a68ce2d1eeb5~mv2.jpg",
    excerpt: "Mayker Financial in Victoria offers personalized, community-focused financial planning, covering investments, tax, insurance, estate planning, and education.",
    link: "https://www.maykerfinancial.com/post/why-local-financial-expertise-matters-how-mayker-financial-supports-victoria-families",
    date: "September 2024"
  }
];

const BlogPage = () => {
  useEffect(() => {
    document.title = "Blog | Mayker Financial Group";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Financial insights for BC families. Practical advice on life insurance, group benefits, investments, and financial planning from Mayker Financial Group.");
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="bg-[#0C1B33] pt-28 lg:pt-32 pb-12 lg:pb-16 px-4 lg:px-12 relative text-center">
        <div className="hero-bottom-line" />
        <div className="max-w-2xl mx-auto">
          <FadeInSection>
            <h1 className="font-playfair text-3xl lg:text-5xl font-bold text-white leading-tight mb-4">
              Financial insights for <em className="italic text-[#C9A84C]">BC families</em>
            </h1>
            <p className="text-base lg:text-lg text-white/60 leading-relaxed">
              Practical advice on life insurance, group benefits, investments, and financial planning.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="bg-[#FAF8F3] py-12 lg:py-16 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {blogPosts.map((post, index) => (
              <FadeInSection key={index}>
                <a 
                  href={post.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white rounded-lg overflow-hidden border border-black/5 h-full service-card"
                >
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5 lg:p-6">
                    <div className="flex items-center gap-2 text-xs text-[#6B7A99] mb-3">
                      <Calendar className="w-3.5 h-3.5" />
                      {post.date}
                    </div>
                    <h2 className="font-playfair text-lg font-semibold text-[#0C1B33] mb-2 leading-snug">
                      {post.title}
                    </h2>
                    <p className="text-sm text-[#6B7A99] leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-[#C9A84C]">
                      Read more <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </a>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0C1B33] py-12 lg:py-16 px-4 lg:px-12 text-center">
        <FadeInSection>
          <div className="max-w-xl mx-auto">
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-3">
              Want personalized advice?
            </h2>
            <p className="text-white/60 mb-6">
              Book a free call with our team and get guidance tailored to your situation.
            </p>
            <Link 
              to="/contact"
              className="inline-block bg-[#C9A84C] text-[#0C1B33] text-sm font-medium tracking-wide py-3.5 px-8 rounded hover:bg-[#E8CC80] transition-colors"
            >
              Book a Free Call →
            </Link>
          </div>
        </FadeInSection>
      </section>
    </div>
  );
};

export default BlogPage;
